import {
    Building2,
    CheckCircle,
    Clock,
    Edit,
    Eye,
    Key,
    Plus,
    Search,
    Trash2,
    UserPlus,
    X
} from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import ManagementServiceBilling from '../../components/BillingManagement/ManagementServiceBilling';
import { formatManagementServicePlan } from '../../components/BillingManagement/ManagementServicePlanPolicy';
import { useAuth } from '../../contexts/AuthContext';
import { useUserRole } from '../../hooks/useUserRole';
import api from '../../services/api';
import PropertyOwnerAccounts from '../PropertyOwnerAccounts/PropertyOwnerAccounts';

const PropertyOwners = () => {
  const { user: currentUser } = useAuth();
  const { isRentalManagementCompany } = useUserRole();
  
  // 디버깅: 탭 표시 조건 확인
  console.log('🔍 PropertyOwners 탭 표시 조건:', {
    currentUser,
    role: currentUser?.role,
    isRentalManagementCompany
  });
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [selectedPropertyOwner, setSelectedPropertyOwner] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('list'); // 'list', 'accounts', 'billing', 'service-plans'
  const queryClient = useQueryClient();

  // 임대사업자 목록 조회
  const { data: propertyOwners, isLoading, error } = useQuery(
    ['property-owners', currentUser?.role],
    async () => {
      console.log('🔍 currentUser:', currentUser);
      console.log('🔍 currentUser?.role:', currentUser?.role);
      
      // 사용자 역할에 따라 다른 API 사용
      let apiUrl = '/property-owners';
      
      // RMC 사용자는 RMC 전용 API 사용
      if (currentUser?.role === 'rental_management_company' || 
          currentUser?.role === 'rmc_admin' || 
          currentUser?.role === 'rmc_staff') {
        apiUrl = '/rmc/property-owners';
      }

      console.log('🔍 사용할 API URL:', apiUrl);
      const res = await api.get(apiUrl);
      console.log('🔍 API 응답:', res);
      console.log('🔍 응답 타입:', typeof res);
      console.log('🔍 배열 여부:', Array.isArray(res));

      // API 인터셉터가 이미 response.data를 반환하므로 res 자체가 데이터
      if (Array.isArray(res)) {
        console.log('✅ 데이터가 배열입니다. 길이:', res.length);
        return res;
      } else {
        console.log('❌ 데이터가 배열이 아닙니다:', res);
        return [];
      }
    },
    {
      enabled: !!currentUser,
      retry: false,
      onError: (error) => {
        console.error('임대사업자 목록 조회 실패:', error);
        toast.error('임대사업자 목록을 불러오는데 실패했습니다.');
      }
    }
  );

  // 임대사업자 등록
  const createPropertyOwnerMutation = useMutation(
    (data) => api.post('/property-owners', data),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('property-owners');
        setShowCreateModal(false);
        toast.success('임대사업자가 등록되었습니다.');

        // 로그인 정보가 있으면 알림
        if (data.login_info) {
          alert(`✅ 임대사업자 등록 완료!\n\n📧 로그인 이메일: ${data.login_info.email}\n🔑 로그인 비밀번호: ${data.login_info.password}\n\n💡 이 정보를 안전한 곳에 보관하고, 생성된 계정으로 바로 로그인할 수 있습니다.`);
        }

        // 강제 새로고침으로 목록 업데이트
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '임대사업자 등록에 실패했습니다.');
      }
    }
  );

  // 계정 생성
  const createAccountMutation = useMutation(
    ({ poId, data }) => api.post(`/property-owners/${poId}/create-account`, data),
    {
      onSuccess: (data) => {
        queryClient.removeQueries('property-owners');
        queryClient.invalidateQueries('property-owners');
        setShowCreateAccountModal(false);
        
        // 토스트 메시지 제거하고 알림창만 사용
        const loginInfo = data.data.login_info;
        alert(`✅ 계정 생성 완료!\n\n📧 로그인 이메일: ${loginInfo.email}\n🔑 로그인 비밀번호: ${loginInfo.password}\n\n💡 이 정보를 안전한 곳에 보관하고, 생성된 계정으로 바로 로그인할 수 있습니다.`);
        
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '계정 생성에 실패했습니다.');
      }
    }
  );

  // 비밀번호 재설정
  const resetPasswordMutation = useMutation(
    ({ poId, data }) => api.post(`/property-owners/${poId}/reset-password`, data),
    {
      onSuccess: (data) => {
        setShowResetPasswordModal(false);
        toast.success('비밀번호가 재설정되었습니다!');
        // api.js의 인터셉터가 response.data를 반환하므로 data.login_info로 접근
        const loginInfo = data.login_info;
        if (loginInfo) {
          alert(`✅ 비밀번호 재설정 완료!\n\n📧 로그인 이메일: ${loginInfo.email}\n🔑 새 비밀번호: ${loginInfo.password}\n\n💡 이 정보를 안전한 곳에 보관하세요.`);
        } else {
          alert(`✅ 비밀번호 재설정 완료!`);
        }
      },
      onError: (error) => {
        console.error('비밀번호 재설정 오류:', error);
        toast.error(error.response?.data?.message || '비밀번호 재설정에 실패했습니다.');
      }
    }
  );

  // 임대사업자 수정
  const updatePropertyOwnerMutation = useMutation(
    ({ poId, data }) => api.put(`/property-owners/${poId}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('property-owners');
        setShowEditModal(false);
        toast.success('임대사업자 정보가 수정되었습니다.');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '임대사업자 수정에 실패했습니다.');
      }
    }
  );

  // 임대사업자 삭제
  const deletePropertyOwnerMutation = useMutation(
    (poId) => api.delete(`/property-owners/${poId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('property-owners');
        toast.success('임대사업자가 삭제되었습니다.');
      },
      onError: (error) => {
        console.error('삭제 오류:', error);
        const errorMessage = error.response?.data?.message || '임대사업자 삭제에 실패했습니다.';
        toast.error(`삭제 실패: ${errorMessage}`);
        alert(`삭제 실패!\n\n오류: ${errorMessage}\n\n상태: ${error.response?.status}\n\n자세한 내용은 개발자 도구 콘솔을 확인하세요.`);
      }
    }
  );

  // 활성화 상태 배지
  const getActivationStatusBadge = (status) => {
    switch (status) {
      case 'pending_activation':
      case 'account_created':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <Clock className="mr-1 h-3 w-3" /> 계정 생성 필요
        </span>;
      case 'active':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="mr-1 h-3 w-3" /> 사용 가능
        </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          {status}
        </span>;
    }
  };

  // 필터링된 목록
  console.log('🔍 propertyOwners:', propertyOwners);
  console.log('🔍 isLoading:', isLoading);
  console.log('🔍 error:', error);
  
  const filteredPropertyOwners = propertyOwners?.filter(po => {
    console.log('🔍 필터링 중인 임대사업자:', po);
    return po.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      po.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      po.email.toLowerCase().includes(searchTerm.toLowerCase());
  }) || [];
  
  console.log('🔍 filteredPropertyOwners:', filteredPropertyOwners);

  // 통계
  const totalPropertyOwners = propertyOwners?.length || 0;
  const pendingActivation = propertyOwners?.filter(po =>
    po.activation_status === 'pending_activation' || po.activation_status === 'account_created'
  ).length || 0;
  const activePropertyOwners = propertyOwners?.filter(po =>
    po.activation_status === 'active'
  ).length || 0;

  const { data: servicePlansData } = useQuery(
    'management-service-plans',
    async () => {
      const res = await api.get('/management-service-billing/plans');
      return res.plans || res || [];
    },
    {
      enabled: !!currentUser,
      onError: (error) => {
        console.error('요금정책 조회 실패:', error);
      }
    }
  );
  const servicePlans = servicePlansData || [];

  const {
    data: propertyOwnerDetail,
    isLoading: isDetailLoading,
    error: detailError
  } = useQuery(
    ['property-owner-detail', selectedPropertyOwner?.id],
    () => api.get(`/property-owners/${selectedPropertyOwner.id}`),
    {
      enabled: showDetailModal && !!selectedPropertyOwner?.id,
      retry: false,
      onError: (error) => {
        console.error('임대사업자 상세 조회 실패:', error);
        toast.error('임대사업자 상세 정보를 불러오는데 실패했습니다.');
      }
    }
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 헤더 */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">
            {currentUser?.role === 'rental_management_company' ? '소속 임대사업자 관리' : '임대사업자 관리'}
          </h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600">
            {currentUser?.role === 'rental_management_company'
              ? '소속 임대사업자들을 등록하고 관리하며 계정을 생성할 수 있습니다.'
              : '등록된 임대사업자들을 관리하고 계정을 생성할 수 있습니다.'}
          </p>
        </div>

        {/* 탭 네비게이션 */}
        <div className="bg-white rounded-lg shadow mb-4 sm:mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-4 sm:space-x-8 px-4 sm:px-6 overflow-x-auto">
              <button
                onClick={() => setActiveTab('list')}
                className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'list'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                임대사업자 목록
              </button>
              <button
                onClick={() => setActiveTab('accounts')}
                className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'accounts'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                계좌 관리
              </button>
              <button
                onClick={() => setActiveTab('billing')}
                className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'billing'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                수수료청구/수납처리
              </button>
              <button
                onClick={() => setActiveTab('service-plans')}
                className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'service-plans'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                임대관리수수료 정책 적용
              </button>
            </nav>
          </div>
        </div>

        {/* 통계 카드 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Building2 className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-600">총 등록 고객사</p>
                <dd className="text-base sm:text-lg font-medium text-gray-900">{totalPropertyOwners}</dd>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-600" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-600">계정 생성 대기</p>
                <dd className="text-base sm:text-lg font-medium text-gray-900">{pendingActivation}</dd>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-600">사용 가능</p>
                <dd className="text-base sm:text-lg font-medium text-gray-900">{activePropertyOwners}</dd>
              </div>
            </div>
          </div>
        </div>

        {/* 탭별 내용 렌더링 */}
        {activeTab === 'list' && (
          <>
            {/* 검색 및 등록 버튼 */}
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 sm:mb-6">
              <div className="relative w-full sm:w-96 mb-3 sm:mb-0">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 sm:h-4 sm:w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="임대사업자 검색..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>

              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-3 sm:px-4 py-2 border border-transparent text-xs sm:text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 w-full sm:w-auto"
              >
                <Plus className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                임대사업자 등록
              </button>
            </div>
          </>
        )}

        {activeTab === 'accounts' && (
          <div className="mb-6">
            <PropertyOwnerAccounts />
          </div>
        )}

        {activeTab === 'billing' && (
          <div className="mb-6">
            <ManagementServiceBilling />
          </div>
        )}

        {activeTab === 'service-plans' && (
          <div className="mb-6">
            <ServicePlanApplicationGuide />
          </div>
        )}

        {/* 임대사업자 목록 */}
        {activeTab === 'list' && (
          <div className="bg-white shadow rounded-lg">
          {(() => {
            console.log('🔍 렌더링 조건 확인:');
            console.log('  - isLoading:', isLoading);
            console.log('  - filteredPropertyOwners.length:', filteredPropertyOwners.length);
            console.log('  - filteredPropertyOwners:', filteredPropertyOwners);
            
            if (isLoading) {
              return (
                <div className="p-8 text-center">
                  <div className="loading-spinner mx-auto"></div>
                  <p className="mt-2 text-gray-600">임대사업자 목록을 불러오는 중...</p>
                </div>
              );
            } else if (filteredPropertyOwners.length === 0) {
              return (
                <div className="p-8 text-center">
                  <Building2 className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">임대사업자가 없습니다</h3>
                  <p className="mt-1 text-sm text-gray-500">새로운 임대사업자를 등록해보세요.</p>
                  <p className="mt-1 text-xs text-gray-400">디버그: propertyOwners = {JSON.stringify(propertyOwners)}</p>
                </div>
              );
            } else {
              return (
            <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 xl:grid-cols-3">
              {filteredPropertyOwners.map((po) => (
                <article
                  key={po.id}
                  className="flex min-h-[260px] flex-col justify-between rounded-lg border border-gray-200 bg-white p-5 shadow-sm transition hover:border-blue-200 hover:shadow-md"
                >
                  <div className="space-y-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-base font-semibold text-gray-900">
                          {po.company_name || po.name || '-'}
                        </p>
                        <p className="mt-1 text-sm text-gray-500">
                          대표자 {po.representative_name || po.name || '-'}
                        </p>
                      </div>
                      <div className="shrink-0">
                        {getActivationStatusBadge(po.activation_status)}
                      </div>
                    </div>

                    <dl className="grid grid-cols-1 gap-3 text-sm">
                      <div>
                        <dt className="text-xs font-medium text-gray-500">사업자등록번호</dt>
                        <dd className="mt-1 font-medium text-gray-900">{po.business_number || '-'}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-gray-500">연락처</dt>
                        <dd className="mt-1 space-y-1 text-gray-900">
                          <div className="truncate">{po.email || '-'}</div>
                          <div className="text-gray-500">{po.phone || '-'}</div>
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-gray-500">등록일</dt>
                        <dd className="mt-1 text-gray-900">{formatDate(po.created_at)}</dd>
                      </div>
                    </dl>
                  </div>

                  <div className="mt-5 flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-4">
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        title="상세보기"
                        onClick={() => {
                          setSelectedPropertyOwner(po);
                          setShowDetailModal(true);
                        }}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                      >
                        <Eye className="h-4 w-4" />
                      </button>

                      <button
                        type="button"
                        title="수정"
                        onClick={() => {
                          setSelectedPropertyOwner(po);
                          setShowEditModal(true);
                        }}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-blue-200 text-blue-600 hover:bg-blue-50 hover:text-blue-900"
                      >
                        <Edit className="h-4 w-4" />
                      </button>

                      <button
                        type="button"
                        title="삭제"
                        onClick={() => {
                          if (window.confirm('정말로 이 임대사업자를 삭제하시겠습니까?')) {
                            deletePropertyOwnerMutation.mutate(po.id);
                          }
                        }}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-red-200 text-red-600 hover:bg-red-50 hover:text-red-900"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    {(po.activation_status === 'pending_activation' || po.activation_status === 'account_created') && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedPropertyOwner(po);
                          setShowCreateAccountModal(true);
                        }}
                        className="inline-flex min-h-[36px] items-center justify-center rounded-md border border-blue-300 px-3 text-xs font-medium text-blue-700 hover:bg-blue-50"
                      >
                        <UserPlus className="mr-1 h-4 w-4" />
                        관리자 계정 만들기
                      </button>
                    )}

                    {po.activation_status === 'active' && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedPropertyOwner(po);
                          setShowResetPasswordModal(true);
                        }}
                        className="inline-flex min-h-[36px] items-center justify-center rounded-md border border-orange-300 px-3 text-xs font-medium text-orange-700 hover:bg-orange-50"
                      >
                        <Key className="mr-1 h-4 w-4" />
                        비밀번호 재설정
                      </button>
                    )}
                  </div>
                </article>
              ))}
            </div>
              );
            }
          })()}
        </div>
        )}

      </div>

      {/* 임대사업자 등록 모달 */}
      {showCreateModal && (
        <CreatePropertyOwnerModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={(data) => createPropertyOwnerMutation.mutate(data)}
          isLoading={createPropertyOwnerMutation.isLoading}
          servicePlans={servicePlans}
        />
      )}

      {/* 임대사업자 수정 모달 */}
      {showEditModal && selectedPropertyOwner && (
        <EditPropertyOwnerModal
          propertyOwner={selectedPropertyOwner}
          onClose={() => setShowEditModal(false)}
          onSubmit={(data) => updatePropertyOwnerMutation.mutate({ poId: selectedPropertyOwner.id, data })}
          isLoading={updatePropertyOwnerMutation.isLoading}
          servicePlans={servicePlans}
        />
      )}

      {/* 계정 생성 모달 */}
      {showCreateAccountModal && selectedPropertyOwner && (
        <CreateAccountModal
          propertyOwner={selectedPropertyOwner}
          onClose={() => setShowCreateAccountModal(false)}
          onSubmit={(data) => createAccountMutation.mutate({ poId: selectedPropertyOwner.id, data })}
          isLoading={createAccountMutation.isLoading}
        />
      )}

      {/* 비밀번호 재설정 모달 */}
      {showResetPasswordModal && selectedPropertyOwner && (
        <ResetPasswordModal
          propertyOwner={selectedPropertyOwner}
          onClose={() => setShowResetPasswordModal(false)}
          onSubmit={(data) => resetPasswordMutation.mutate({ poId: selectedPropertyOwner.id, data })}
          isLoading={resetPasswordMutation.isLoading}
        />
      )}

      {showDetailModal && selectedPropertyOwner && (
        <PropertyOwnerDetailModal
          propertyOwner={propertyOwnerDetail || selectedPropertyOwner}
          isLoading={isDetailLoading}
          error={detailError}
          onClose={() => setShowDetailModal(false)}
          onEdit={() => {
            setShowDetailModal(false);
            setShowEditModal(true);
          }}
        />
      )}
    </div>
  );
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('ko-KR');
};

const getDisplayValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return value;
};

const DetailItem = ({ label, value }) => (
  <div>
    <dt className="text-xs font-medium text-gray-500">{label}</dt>
    <dd className="mt-1 text-sm text-gray-900 break-words">{getDisplayValue(value)}</dd>
  </div>
);

const PropertyOwnerDetailModal = ({ propertyOwner, isLoading, error, onClose, onEdit }) => {
  const adminUser = propertyOwner?.admin_user;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative mx-auto my-8 w-full max-w-3xl px-4">
        <div className="bg-white rounded-lg shadow-lg">
          <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4">
            <div>
              <h3 className="text-lg font-medium text-gray-900">임대사업자 상세정보</h3>
              <p className="mt-1 text-sm text-gray-500">{getDisplayValue(propertyOwner?.company_name || propertyOwner?.name)}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              title="닫기"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="px-5 py-5">
            {isLoading ? (
              <div className="py-10 text-center">
                <div className="loading-spinner mx-auto"></div>
                <p className="mt-3 text-sm text-gray-600">상세 정보를 불러오는 중...</p>
              </div>
            ) : error ? (
              <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                상세 정보를 불러오지 못했습니다. 잠시 후 다시 시도해주세요.
              </div>
            ) : (
              <div className="space-y-6">
                <section>
                  <h4 className="text-sm font-semibold text-gray-900">기본 정보</h4>
                  <dl className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <DetailItem label="회사명" value={propertyOwner?.company_name || propertyOwner?.name} />
                    <DetailItem label="대표자명" value={propertyOwner?.representative_name} />
                    <DetailItem label="사업자등록번호" value={propertyOwner?.business_number} />
                    <DetailItem label="상태" value={propertyOwner?.activation_status} />
                    <DetailItem label="등록일" value={formatDate(propertyOwner?.created_at)} />
                    <DetailItem label="활성화일" value={formatDate(propertyOwner?.activated_at)} />
                  </dl>
                </section>

                <section>
                  <h4 className="text-sm font-semibold text-gray-900">연락처 및 주소</h4>
                  <dl className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <DetailItem label="이메일" value={propertyOwner?.email} />
                    <DetailItem label="전화번호" value={propertyOwner?.phone} />
                    <div className="sm:col-span-2">
                      <DetailItem label="주소" value={propertyOwner?.address} />
                    </div>
                  </dl>
                </section>

                <section>
                  <h4 className="text-sm font-semibold text-gray-900">서비스 설정</h4>
                  <dl className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <DetailItem label="기본 요금정책" value={propertyOwner?.default_service_plan_name} />
                    <DetailItem label="요금정책 ID" value={propertyOwner?.default_service_plan_id} />
                  </dl>
                </section>

                <section>
                  <h4 className="text-sm font-semibold text-gray-900">관리자 계정</h4>
                  {adminUser ? (
                    <dl className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <DetailItem label="관리자명" value={adminUser.name} />
                      <DetailItem label="관리자 이메일" value={adminUser.email} />
                      <DetailItem label="관리자 전화번호" value={adminUser.phone} />
                      <DetailItem label="사용자 ID" value={adminUser.id} />
                    </dl>
                  ) : (
                    <p className="mt-3 rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">
                      연결된 관리자 계정 정보가 없습니다.
                    </p>
                  )}
                </section>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 border-t border-gray-200 px-5 py-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              닫기
            </button>
            <button
              type="button"
              onClick={onEdit}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Edit className="mr-2 h-4 w-4" />
              수정
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// 임대사업자 등록 모달
const CreatePropertyOwnerModal = ({ onClose, onSubmit, isLoading, servicePlans = [] }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    business_number: '',
    representative_name: '',
    company_name: '',
    initial_password: '',
    service_plan_id: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      service_plan_id: formData.service_plan_id ? parseInt(formData.service_plan_id, 10) : null
    });
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">임대사업자 등록</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">회사명</label>
              <input
                type="text"
                required
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value, name: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">대표자명</label>
              <input
                type="text"
                required
                value={formData.representative_name}
                onChange={(e) => setFormData({ ...formData, representative_name: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">사업자등록번호</label>
              <input
                type="text"
                required
                value={formData.business_number}
                onChange={(e) => setFormData({ ...formData, business_number: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                이메일 <span className="text-xs text-gray-500">(Login ID로 사용됩니다)</span>
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">초기 비밀번호</label>
              <input
                type="text"
                required
                value={formData.initial_password}
                onChange={(e) => setFormData({ ...formData, initial_password: e.target.value })}
                placeholder="6자 이상 입력하세요"
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">전화번호</label>
              <input
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">주소</label>
              <input
                type="text"
                required
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">기본 임대관리요금정책</label>
              <select
                value={formData.service_plan_id}
                onChange={(e) => setFormData({ ...formData, service_plan_id: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">선택 안함</option>
                {servicePlans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.plan_name} ({formatManagementServicePlan(plan)})
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                정책은 공통모듈관리의 임대관리요금정책 설정에서 등록합니다.
              </p>
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {isLoading ? '등록 중...' : '등록'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// 계정 생성 모달
const CreateAccountModal = ({ propertyOwner, onClose, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    admin_name: '',
    admin_email: '',
    admin_phone: '',
    admin_password: '',
    confirm_password: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.admin_password !== formData.confirm_password) {
      toast.error('비밀번호가 일치하지 않습니다.');
      return;
    }
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">관리자 계정 생성</h3>
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800">
              <strong>{propertyOwner.company_name}</strong>의 관리자 계정을 생성합니다.
            </p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
            <div>
              <label className="block text-sm font-medium text-gray-700">관리자 이름</label>
              <input
                type="text"
                name="admin_name"
                autoComplete="name"
                required
                value={formData.admin_name}
                onChange={(e) => setFormData({ ...formData, admin_name: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">관리자 이메일</label>
              <input
                type="email"
                name="admin_email"
                autoComplete="email"
                required
                value={formData.admin_email}
                onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">관리자 전화번호</label>
              <input
                type="tel"
                name="admin_phone"
                autoComplete="tel"
                required
                value={formData.admin_phone}
                onChange={(e) => setFormData({ ...formData, admin_phone: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">비밀번호</label>
              <input
                type="password"
                name="admin_password"
                autoComplete="new-password"
                required
                minLength={6}
                value={formData.admin_password}
                onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">비밀번호 확인</label>
              <input
                type="password"
                name="confirm_password"
                autoComplete="new-password"
                required
                minLength={6}
                value={formData.confirm_password}
                onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="p-3 bg-green-50 border border-green-200 rounded-md">
              <p className="text-sm text-green-800">
                💡 계정 생성 후 바로 로그인할 수 있습니다!
              </p>
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {isLoading ? '생성 중...' : '계정 생성'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// 비밀번호 재설정 모달
const ResetPasswordModal = ({ propertyOwner, onClose, onSubmit, isLoading }) => {
  const [newPassword, setNewPassword] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('비밀번호는 6자 이상이어야 합니다.');
      return;
    }
    onSubmit({ new_password: newPassword });
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">비밀번호 재설정</h3>
          <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-md">
            <p className="text-sm text-orange-800">
              <strong>{propertyOwner.company_name}</strong>의 관리자 비밀번호를 재설정합니다.
            </p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">새 비밀번호</label>
              <input
                type="password"
                required
                minLength={6}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50"
              >
                {isLoading ? '재설정 중...' : '비밀번호 재설정'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// 임대사업자 수정 모달
const EditPropertyOwnerModal = ({ propertyOwner, onClose, onSubmit, isLoading, servicePlans = [] }) => {
  const [formData, setFormData] = useState({
    company_name: propertyOwner.company_name || '',
    representative_name: propertyOwner.representative_name || '',
    phone: propertyOwner.phone || '',
    address: propertyOwner.address || '',
    service_plan_id: propertyOwner.default_service_plan_id || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      service_plan_id: formData.service_plan_id ? parseInt(formData.service_plan_id, 10) : null
    });
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">임대사업자 정보 수정</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">회사명</label>
              <input
                type="text"
                required
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">대표자명</label>
              <input
                type="text"
                required
                value={formData.representative_name}
                onChange={(e) => setFormData({ ...formData, representative_name: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">전화번호</label>
              <input
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">주소</label>
              <input
                type="text"
                required
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">기본 임대관리요금정책</label>
              <select
                value={formData.service_plan_id}
                onChange={(e) => setFormData({ ...formData, service_plan_id: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">선택 안함</option>
                {servicePlans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.plan_name} ({formatManagementServicePlan(plan)})
                  </option>
                ))}
              </select>
            </div>
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-800">
                💡 이메일과 사업자등록번호는 수정할 수 없습니다.
              </p>
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {isLoading ? '수정 중...' : '수정'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const ServicePlanApplicationGuide = () => (
  <div className="space-y-6">
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">임대관리수수료 정책 적용</h2>
          <p className="mt-1 text-sm text-gray-600">
            요금정책 등록은 공통모듈관리의 임대관리요금정책 설정에서 관리합니다.
            이 화면에서는 등록된 정책을 임대사업자, 건물, 세대, 청구서에 적용하는 흐름으로 사용합니다.
          </p>
        </div>
        <Link
          to="/common-modules"
          className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          공통모듈관리로 이동
        </Link>
      </div>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
        <p className="text-sm font-semibold text-blue-900">1. 정책 등록</p>
        <p className="mt-2 text-sm text-blue-700">
          공통모듈관리에서 세대당 수수료, 청구주기, 납부기한을 가진 기본 요금정책을 등록합니다.
        </p>
      </div>
      <div className="bg-green-50 border border-green-100 rounded-lg p-4">
        <p className="text-sm font-semibold text-green-900">2. 대상 적용</p>
        <p className="mt-2 text-sm text-green-700">
          임대사업자 등록 시 기본 요금정책을 선택하거나, 세대 등록/수정 화면에서 세대별 요금정책을 지정합니다.
        </p>
      </div>
      <div className="bg-purple-50 border border-purple-100 rounded-lg p-4">
        <p className="text-sm font-semibold text-purple-900">3. 청구 생성</p>
        <p className="mt-2 text-sm text-purple-700">
          수수료 청구 화면에서 적용된 정책을 기준으로 청구서를 생성하고 발행/수납 처리합니다.
        </p>
      </div>
    </div>
  </div>
);

// 임대관리수수료 요금플랜 관리 컴포넌트
// eslint-disable-next-line no-unused-vars
const ManagementServicePlans = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const queryClient = useQueryClient();

  // 요금플랜 목록 조회
  const { data: plansData, isLoading } = useQuery(
    'management-service-plans',
    async () => {
      const res = await api.get('/management-service-billing/plans');
      return res.plans || res || [];
    },
    {
      onError: (error) => {
        console.error('요금플랜 조회 실패:', error);
        toast.error('요금플랜 목록을 불러오는데 실패했습니다.');
      }
    }
  );

  const plans = plansData || [];

  // 요금플랜 생성
  const createPlanMutation = useMutation(
    (data) => api.post('/management-service-billing/plans', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('management-service-plans');
        setShowCreateModal(false);
        toast.success('요금플랜이 등록되었습니다.');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '요금플랜 등록에 실패했습니다.');
      }
    }
  );

  // 요금플랜 수정
  const updatePlanMutation = useMutation(
    ({ planId, data }) => api.put(`/management-service-billing/plans/${planId}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('management-service-plans');
        setShowEditModal(false);
        setSelectedPlan(null);
        toast.success('요금플랜이 수정되었습니다.');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '요금플랜 수정에 실패했습니다.');
      }
    }
  );

  // 요금플랜 삭제
  const deletePlanMutation = useMutation(
    (planId) => api.delete(`/management-service-billing/plans/${planId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('management-service-plans');
        toast.success('요금플랜이 삭제되었습니다.');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '요금플랜 삭제에 실패했습니다.');
      }
    }
  );

  const handleDelete = (planId) => {
    if (window.confirm('정말로 이 요금플랜을 삭제하시겠습니까?')) {
      deletePlanMutation.mutate(planId);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <div className="loading-spinner mx-auto"></div>
        <p className="mt-2 text-gray-600">요금플랜 목록을 불러오는 중...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 헤더 및 등록 버튼 */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-gray-900">임대관리수수료 요금플랜</h2>
          <p className="mt-1 text-sm text-gray-600">임대사업자에게 적용할 요금플랜을 관리합니다.</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="mr-2 h-4 w-4" />
          요금플랜 등록
        </button>
      </div>

      {/* 요금플랜 목록 */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {plans.length === 0 ? (
          <div className="p-8 text-center">
            <Building2 className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">등록된 요금플랜이 없습니다</h3>
            <p className="mt-1 text-sm text-gray-500">새로운 요금플랜을 등록해보세요.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    카테고리
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    요금플랜명
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    세대당 수수료
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    청구 주기
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    상태
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    작업
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {plans.map((plan) => (
                  <tr key={plan.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        plan.plan_category === 'economy' ? 'bg-gray-100 text-gray-800' :
                        plan.plan_category === 'basic' ? 'bg-blue-100 text-blue-800' :
                        plan.plan_category === 'regular' ? 'bg-green-100 text-green-800' :
                        plan.plan_category === 'premium' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {plan.plan_category === 'economy' ? 'Economy' :
                         plan.plan_category === 'basic' ? 'Basic' :
                         plan.plan_category === 'regular' ? 'Regular' :
                         plan.plan_category === 'premium' ? 'Premium' : plan.plan_category || 'Regular'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{plan.plan_name}</div>
                      {plan.description && (
                        <div className="text-sm text-gray-500">{plan.description}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {Number(plan.base_fee_per_unit || 0).toLocaleString()}원
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {plan.billing_cycle === 'monthly' ? '월간' : 
                       plan.billing_cycle === 'quarterly' ? '분기' : 
                       plan.billing_cycle === 'annual' ? '연간' : plan.billing_cycle}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {plan.is_active ? (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          활성
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                          비활성
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => {
                            setSelectedPlan(plan);
                            setShowEditModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(plan.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 요금플랜 등록 모달 */}
      {showCreateModal && (
        <ServicePlanModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={(data) => createPlanMutation.mutate(data)}
          isLoading={createPlanMutation.isLoading}
        />
      )}

      {/* 요금플랜 수정 모달 */}
      {showEditModal && selectedPlan && (
        <ServicePlanModal
          plan={selectedPlan}
          onClose={() => {
            setShowEditModal(false);
            setSelectedPlan(null);
          }}
          onSubmit={(data) => updatePlanMutation.mutate({ planId: selectedPlan.id, data })}
          isLoading={updatePlanMutation.isLoading}
        />
      )}
    </div>
  );
};

// 요금플랜 등록/수정 모달
const ServicePlanModal = ({ plan, onClose, onSubmit, isLoading }) => {
  const isEdit = !!plan;
  const [formData, setFormData] = useState({
    plan_name: plan?.plan_name || '',
    description: plan?.description || '',
    plan_category: plan?.plan_category || 'regular',
    base_fee_per_unit: plan?.base_fee_per_unit || '',
    billing_cycle: plan?.billing_cycle || 'monthly',
    billing_day: plan?.billing_day || 1,
    due_days: plan?.due_days || 7,
    effective_from: plan?.effective_from || '',
    effective_to: plan?.effective_to || '',
    additional_fees: plan?.additional_fees ? (typeof plan.additional_fees === 'string' ? JSON.parse(plan.additional_fees) : plan.additional_fees) : {},
    discount_settings: plan?.discount_settings ? (typeof plan.discount_settings === 'string' ? JSON.parse(plan.discount_settings) : plan.discount_settings) : {}
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      base_fee_per_unit: parseFloat(formData.base_fee_per_unit),
      billing_day: parseInt(formData.billing_day),
      due_days: parseInt(formData.due_days),
      effective_from: formData.effective_from || null,
      effective_to: formData.effective_to || null,
      additional_fees: formData.additional_fees || {},
      discount_settings: formData.discount_settings || {}
    };
    onSubmit(submitData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {isEdit ? '요금플랜 수정' : '요금플랜 등록'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">요금플랜명 *</label>
              <input
                type="text"
                required
                value={formData.plan_name}
                onChange={(e) => setFormData({ ...formData, plan_name: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">설명</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">요금플랜 카테고리 *</label>
              <select
                value={formData.plan_category}
                onChange={(e) => setFormData({ ...formData, plan_category: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                required
              >
                <option value="economy">Economy (경제형)</option>
                <option value="basic">Basic (기본형)</option>
                <option value="regular">Regular (일반형)</option>
                <option value="premium">Premium (프리미엄)</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">세대당 기본 수수료 (원) *</label>
                <input
                  type="number"
                  required
                  min="0"
                  step="0.01"
                  value={formData.base_fee_per_unit}
                  onChange={(e) => setFormData({ ...formData, base_fee_per_unit: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">청구 주기 *</label>
                <select
                  value={formData.billing_cycle}
                  onChange={(e) => setFormData({ ...formData, billing_cycle: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="monthly">월간</option>
                  <option value="quarterly">분기</option>
                  <option value="annual">연간</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">청구일 (1-28일)</label>
                <input
                  type="number"
                  min="1"
                  max="28"
                  value={formData.billing_day}
                  onChange={(e) => setFormData({ ...formData, billing_day: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">납부기한 (일)</label>
                <input
                  type="number"
                  min="1"
                  value={formData.due_days}
                  onChange={(e) => setFormData({ ...formData, due_days: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">유효 시작일</label>
                <input
                  type="date"
                  value={formData.effective_from}
                  onChange={(e) => setFormData({ ...formData, effective_from: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">유효 종료일</label>
                <input
                  type="date"
                  value={formData.effective_to}
                  onChange={(e) => setFormData({ ...formData, effective_to: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {isLoading ? (isEdit ? '수정 중...' : '등록 중...') : (isEdit ? '수정' : '등록')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PropertyOwners;
